import { Property, PaginatedResponse } from "@/models";
import { apiRequest, SUPABASE_URL } from "./apiUtils";
import { createApiWrapper } from "./apiWrapper";
import { CSVUploadResponse, ImportJobStatus } from "@/types/upload";

// Create basic CRUD operations
const basicPropertyApi = createApiWrapper<Property>('properties');

// Extended property API with additional methods
export const propertyApi = {
  ...basicPropertyApi,
  
  // Add getById method
  getById: async (id: number): Promise<Property> => {
    const url = `${SUPABASE_URL}/properties/${id}`;
    return apiRequest<Property>(url);
  },
  
  // Override the list method to handle the paginated response
  list: async (params?: { queryKey?: any }): Promise<PaginatedResponse<Property>> => {
    const [_key, searchParams] = params?.queryKey || ['properties', {}];
    const queryParams = new URLSearchParams();
    
    if (searchParams?.search) {
      queryParams.append('search', searchParams.search);
    }
    
    if (searchParams?.association_id) {
      queryParams.append('association_id', searchParams.association_id.toString());
    }
    
    if (searchParams?.limit) {
      queryParams.append('limit', searchParams.limit.toString());
    }
    
    if (searchParams?.offset) {
      queryParams.append('offset', searchParams.offset.toString());
    }
    
    const url = `${SUPABASE_URL}/properties?${queryParams.toString()}`;
    console.log("Fetching properties from:", url);
    return apiRequest<PaginatedResponse<Property>>(url);
  },
  
  // Custom methods for association properties
  getAssociationProperties: async (associationId: number) => 
    apiRequest<Property[]>(`${SUPABASE_URL}/associations/${associationId}/properties`),
    
  setDefaultProperty: async (associationId: number, propertyId: number) => 
    apiRequest<void>(`${SUPABASE_URL}/associations/${associationId}/default-property`, {
      method: 'PUT',
      body: { property_id: propertyId }
    }),
    
  autocompleteProperties: async (query: string) => 
    apiRequest<Property[]>(`${SUPABASE_URL}/properties/autocomplete?query=${encodeURIComponent(query)}`),
    
  bulkCreate: async (associationId: number, properties: Omit<Property, "id" | "created_at" | "updated_at">[]) => 
    apiRequest<Property[]>(`${SUPABASE_URL}/properties/bulk`, {
      method: 'POST',
      body: { 
        association_id: associationId,
        properties 
      }
    }),
    
  bulkImport: async (associationId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('association_id', associationId.toString());
    
    return apiRequest<{ total: number; imported: number; errors: string[] }>(`${SUPABASE_URL}/properties/import`, {
      method: 'POST',
      body: formData
    });
  },
  
  // Updated to match the same pattern as associationApi.bulkUpload
  bulkUpload: async (file: File, associationId?: number) => {
    console.log(`Creating FormData with file: ${file.name}${associationId ? ` and associationId: ${associationId}` : ''}`);
    
    // Create a fresh FormData instance
    const formData = new FormData();
    
    // Add file and association ID to form data
    formData.append('file', file);
    if (associationId) {
      formData.append('association_id', associationId.toString());
    }
    
    console.log('FormData entries:', [...formData.entries()].map(entry => 
      entry[0] === 'file' ? `${entry[0]}: [File: ${(entry[1] as File).name}]` : `${entry[0]}: ${entry[1]}`
    ));
    
    const uploadUrl = `${SUPABASE_URL}/properties/import`;
    console.log('Sending bulk upload request to:', uploadUrl);
    
    try {
      return await apiRequest<{ total: number; imported: number; errors: string[]; jobId?: string }>(`${SUPABASE_URL}/properties/import`, {
        method: 'POST',
        body: formData
      });
    } catch (error) {
      console.error("Error in bulkUpload:", error);
      throw error;
    }
  },
  
  // New function to bulk upload properties with association reference - updated for async operation
  bulkUploadWithAssociation: async (file: File) => {
    console.log(`Creating FormData with file: ${file.name} for async upload`);
    
    // Create a fresh FormData instance
    const formData = new FormData();
    
    // Add file to form data
    formData.append('file', file);
    
    console.log('FormData entries:', [...formData.entries()].map(entry => 
      entry[0] === 'file' ? `${entry[0]}: [File: ${(entry[1] as File).name}]` : `${entry[0]}: ${entry[1]}`
    ));
    
    const uploadUrl = `${SUPABASE_URL}/properties/import-with-association-async`;
    console.log('Sending async bulk upload request to:', uploadUrl);
    
    try {
      return await apiRequest<CSVUploadResponse>(`${SUPABASE_URL}/properties/import-with-association-async`, {
        method: 'POST',
        body: formData
      });
    } catch (error) {
      console.error("Error in async bulkUploadWithAssociation:", error);
      throw error;
    }
  },
  
  // Add method to check job status
  checkImportJobStatus: async (jobId: string) => {
    console.log(`Checking import job status for jobId: ${jobId}`);
    
    const statusUrl = `${SUPABASE_URL}/properties/import-job-status/${jobId}`;
    
    try {
      return await apiRequest<ImportJobStatus>(statusUrl);
    } catch (error) {
      console.error("Error checking job status:", error);
      throw error;
    }
  },
  
  // Export all properties for a management company as CSV - updated to include association_external_id
  exportCsv: async (managementCompanyId: number): Promise<string> => {
    console.log(`Exporting properties CSV for management company ID: ${managementCompanyId}`);
    
    return apiRequest<string>(`${SUPABASE_URL}/properties/export-csv`, {
      method: 'POST',
      body: { management_company_id: managementCompanyId },
      responseType: 'text'
    });
  }
};
